<template>
  <div class="max-w-md mx-auto">
    <h4 class="mb-4 text-gravaadeepbrown">Join the Hub</h4>

    <!-- Email Input Field -->
    <input
      v-model="email"
      @input="validateEmail"
      type="email"
      placeholder="Enter your email address"
      class="w-full mb-2 p-2 border bg-warmgray-light"
      :class="{ 'border-red-600': emailError }"
      :disabled="message === 'Successfully subscribed!' || isProcessing"
    />

    <!-- Email error message -->
    <p v-if="emailError && hasAttemptedSubmit" class="text-sm text-red-600 mb-2">
      {{ emailError }}
    </p>

    <!-- Sign Up Button -->
    <UiButton
      v-if="message !== 'Successfully subscribed!'"
      @click="subscribe"
      :disabled="!email || isProcessing"
      class="transform transition-transform duration-200 hover:scale-110 hover:underline underline-offset-2 text-gravaadeepbrown"
    >
      Sign up for our newsletter
    </UiButton>

    <!-- Success message -->
    <p v-else class="mt-2 text-gravaadeepbrown">{{ message }}</p>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";

// State
const email = ref("");
const emailError = ref(null);
const message = ref(null);
const isProcessing = ref(false);
const hasAttemptedSubmit = ref(false);

// Email Validation
function validateEmail() {
  const emailVal = email.value.trim();
  const strongPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]{3,}\.[a-zA-Z]{2,}$/;

  emailError.value = null;

  if (!emailVal) {
    emailError.value = "Please enter an email address.";
  } else if (emailVal.length < 8) {
    emailError.value = "Email address is too short. Must be at least 8 characters.";
  } else if (emailVal.length > 254) {
    emailError.value = "Email address is too long. Maximum length is 254 characters.";
  } else if (/[\s]/.test(emailVal)) {
    emailError.value = "Email address cannot contain spaces.";
  } else if (!strongPattern.test(emailVal)) {
    emailError.value = "Please enter a valid email address (e.g., user@example.com).";
  } else {
    const domainPart = emailVal.split('@')[1];
    const lastDotIndex = domainPart.lastIndexOf('.');
    if (lastDotIndex === -1 || domainPart.length - lastDotIndex - 1 < 2) {
      emailError.value = "Email domain must include a dot (.) with at least 2 characters after it.";
    }
  }
}

// GraphQL Mutation
const CUSTOMER_CREATE_MUTATION = gql`
  mutation CustomerCreate($input: CustomerCreateInput!) {
    customerCreate(input: $input) {
      customer {
        id
        email
        acceptsMarketing
      }
      customerUserErrors {
        field
        message
        code
      }
    }
  }
`;

const { mutate, onDone, onError } = useMutation(CUSTOMER_CREATE_MUTATION);

// Submit Handler
const subscribe = async () => {
  hasAttemptedSubmit.value = true;
  validateEmail();

  emailError.value = null;
  message.value = null;
  isProcessing.value = true;

  if (emailError.value) {
    isProcessing.value = false;
    return;
  }

  try {
    const result = await mutate({
      input: {
        email: email.value,
        password: "dummyPassword123",
        acceptsMarketing: true,
      },
    });

    const response = result?.data?.customerCreate;

    if (!response) {
      message.value = "Something went wrong. Please try again.";
      isProcessing.value = false;
      return;
    }

    const userErrors = response.customerUserErrors;

    if (userErrors && userErrors.length > 0) {
      const emailErrorMsg = userErrors.find((error) => error.field?.includes("email"));

      if (emailErrorMsg) {
        emailError.value = emailErrorMsg.message || "Something went wrong with your email.";
      } else {
        message.value = userErrors[0]?.message || "An error occurred. Please try again.";
      }

      isProcessing.value = false;
      return;
    }

    // No errors — success!
    message.value = "Successfully subscribed!";
  } catch (error) {
    console.error("GraphQL Error:", error.message);
    message.value = "An unexpected error occurred. Please try again.";
  } finally {
    isProcessing.value = false;
  }
};

// Global error handling
onError((error) => {
  console.error("Subscription Error:", error.message);
  message.value = "An error occurred while subscribing. Please try again.";
});

onDone((data) => {
  console.log("GQL Response:", data);
});
</script>
